import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Menu, X, Search, Phone, Mail, Truck, Shield, User, LogIn, UserPlus, LogOut } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Top utility bar component
const TopBar = ({ businessInfo }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Format phone for tel: link (remove non-digits)
  const phoneLink = businessInfo?.phone?.replace(/\D/g, '') || '8445897377';
  const phoneDisplay = businessInfo?.phone || '(844) 589-PEPS';
  const emailDisplay = businessInfo?.email || 'support@amino-chain.com';

  return (
    <div className="bg-void-base border-b border-purple-900/30 py-2.5 px-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between text-sm">
        {/* Left side - Contact info */}
        <div className="flex items-center gap-6">
          <a href={`tel:+1${phoneLink}`} className="flex items-center gap-2 text-slate-300 hover:text-gold-300 transition-colors duration-300">
            <Phone className="w-4 h-4 text-gold-400" />
            <span className="hidden sm:inline">{phoneDisplay}</span>
          </a>
          <a href={`mailto:${emailDisplay}`} className="hidden md:flex items-center gap-2 text-slate-300 hover:text-gold-300 transition-colors duration-300">
            <Mail className="w-4 h-4 text-gold-400" />
            <span>{emailDisplay}</span>
          </a>
        </div>

        {/* Center - Info ticker */}
        <div className="hidden lg:flex items-center gap-6 text-slate-300">
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-gold-400" />
            <span>Free Shipping $100+</span>
          </div>
          <div className="w-px h-4 bg-purple-700" />
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-gold-400" />
            <span>99%+ Purity Guaranteed</span>
          </div>
        </div>

        {/* Right side - Auth */}
        <div className="flex items-center gap-4">
          {isAuthenticated ? (
            <>
              <Link 
                to={user?.role === 'super_admin' || user?.role === 'admin' ? '/admin' : '/account'}
                className="hidden sm:flex items-center gap-2 text-slate-300 hover:text-gold-300 transition-colors duration-300"
                data-testid="topbar-user-link"
              >
                <User className="w-4 h-4 text-gold-400" />
                <span className="hover:underline">{user?.name || user?.email}</span>
              </Link>
              <button 
                onClick={handleLogout}
                className="flex items-center gap-2 text-slate-300 hover:text-red-400 transition-colors duration-300"
                data-testid="topbar-logout"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </>
          ) : (
            <>
              <Link 
                to="/login" 
                className="flex items-center gap-2 text-slate-300 hover:text-gold-300 transition-colors duration-300"
                data-testid="topbar-login"
              >
                <LogIn className="w-4 h-4" />
                <span className="hidden sm:inline">Login</span>
              </Link>
              <Link 
                to="/register" 
                className="flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-purple-600 to-gold-500 text-white rounded-sm hover:shadow-lg hover:shadow-gold-500/20 transition-all duration-300 font-medium text-xs uppercase tracking-wider"
                data-testid="topbar-register"
              >
                <UserPlus className="w-4 h-4" />
                <span className="hidden sm:inline">Register</span>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const FloatingNav = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const location = useLocation();
  const { cartItems } = useCart();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Fetch business and site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [businessRes, siteRes] = await Promise.all([
          axios.get(`${API_URL}/api/settings/business`),
          axios.get(`${API_URL}/api/settings/site`)
        ]);
        setBusinessInfo(businessRes.data);
        setSiteSettings(siteRes.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Base nav links
  const baseNavLinks = [
    { name: 'Peptides', href: '/premium-peptides' },
    { name: 'Research', href: '/peptides-research' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];
  
  // Add "My Account" for non-admin logged-in users
  const navLinks = isAuthenticated && user?.role !== 'super_admin' && user?.role !== 'admin'
    ? [...baseNavLinks, { name: 'My Account', href: '/account' }]
    : baseNavLinks;

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  return (
    <>
      {/* Top utility bar - fixed at very top */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <TopBar businessInfo={businessInfo} />
      </div>

      {/* Main navigation */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-[41px] left-0 right-0 z-40 transition-all duration-500 ${
          isScrolled 
            ? 'bg-void-surface/95 backdrop-blur-xl shadow-lg shadow-purple-900/20 border-b border-gold-500/20' 
            : 'bg-void-base/80 backdrop-blur-xl border-b border-white/5'
        }`}
        data-testid="floating-nav"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="nav-logo"
          >
            <img
              src={siteSettings?.logo_url || "https://customer-assets.emergentagent.com/job_35efb418-d957-4303-979f-4e5863096b08/artifacts/hzi2b2xm_amino-chain-logo-final-1.png"}
              alt={siteSettings?.site_name || "AMINO-CHAIN"}
              className="h-10 md:h-12 w-auto object-contain group-hover:drop-shadow-[0_0_15px_rgba(185,137,61,0.4)] transition-all duration-300"
              data-testid="nav-logo-image"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`relative px-5 py-2.5 text-sm font-semibold tracking-wide uppercase transition-all duration-300 ${
                  isActive(link.href)
                    ? 'text-gold-200'
                    : 'text-slate-300 hover:text-gold-300'
                }`}
              >
                {link.name}
                {isActive(link.href) && (
                  <motion.div 
                    layoutId="nav-indicator"
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-gradient-to-r from-transparent via-gold-400 to-transparent"
                  />
                )}
              </Link>
            ))}
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Search button */}
            <button 
              className="p-2.5 rounded-lg text-slate-400 hover:text-gold-300 hover:bg-white/5 transition-all duration-300"
              data-testid="nav-search-btn"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Cart */}
            <Link
              to="/checkout"
              className="relative p-2.5 rounded-lg text-slate-400 hover:text-gold-300 hover:bg-white/5 transition-all duration-300"
              data-testid="nav-cart-btn"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-gold-400 to-gold-600 text-void-base text-xs font-bold rounded-full flex items-center justify-center shadow-lg shadow-gold-500/30"
                >
                  {cartCount}
                </motion.span>
              )}
            </Link>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2.5 rounded-lg text-slate-400 hover:text-gold-300 hover:bg-white/5 transition-all duration-300"
              data-testid="nav-mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-[105px] left-4 right-4 z-30 bg-void-surface/95 backdrop-blur-xl border border-gold-500/20 rounded-lg p-4 md:hidden shadow-2xl shadow-purple-900/30"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-semibold uppercase tracking-wider transition-all duration-300 ${
                    isActive(link.href)
                      ? 'bg-gradient-to-r from-purple-600 to-gold-500 text-white'
                      : 'text-slate-300 hover:text-gold-300 hover:bg-white/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="border-t border-purple-800/50 mt-2 pt-2">
                {isAuthenticated ? (
                  <>
                    <div className="px-4 py-2 text-sm text-slate-300">
                      Signed in as {user?.name || user?.email}
                    </div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full px-4 py-3 rounded-lg text-sm font-semibold text-red-400 hover:bg-red-500/10 transition-colors duration-300"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-slate-300 hover:text-gold-300 hover:bg-white/5 transition-colors duration-300"
                    >
                      <LogIn className="w-4 h-4" />
                      Login
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold bg-gradient-to-r from-purple-600 to-gold-500 text-white mt-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      Register to See Prices
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Research Warning Banner - Elegant Gold */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-void-base/95 backdrop-blur-md border-t border-gold-500/30 py-2.5 px-4">
        <p className="text-center text-xs font-mono text-gold-400 tracking-[0.2em] uppercase">
          For Research Use Only • Not For Human Consumption • Must Be 21+
        </p>
      </div>
    </>
  );
};

export default FloatingNav;
